import { RestChatService } from '@repo/network'
import { ComponentUserMenuItemVO } from '@repo/types'
import { useQuery } from '@tanstack/react-query'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { normalize } from '@repo/db'
import { MenuCategory, MenuItem } from '@tecsinapse/cortex-react'

export const useHeaderNav = () => {
  const [menuOptions, setMenuOptions] = useState<MenuCategory[]>([])
  const [filteredMenuOptions, setFilteredMenuOptions] = useState<MenuCategory[]>([])
  const [connectionKey, setConnectionKey] = React.useState<string>('')

  useEffect(() => {
    RestChatService.info().then(it => {
      const connection = it.connectionKeys.shift()

      if (connection) {
        setConnectionKey(connection)
      }
    })
  }, [])

  const { data: userMenu, isFetching: loading } = useQuery({
    queryKey: ['userMenu'],
    queryFn: async () => await RestChatService.userMenu()
  })

  const getMenuItemsOptions = useCallback((item: ComponentUserMenuItemVO): MenuItem => {
    if (item.link) {
      return {
        title: item.title,
        anchorProps: { className: 'no-underline', target: '_self', href: item.link }
      }
    }

    return {
      title: item.title,
      items: item.items?.map(it => getMenuItemsOptions(it))
    }
  }, [])

  useEffect(() => {
    if (userMenu) {
      setMenuOptions(
        userMenu.items.map(it => ({
          title: it.title,
          items: it.items.map(item => getMenuItemsOptions(item))
        }))
      )
    }
  }, [getMenuItemsOptions, userMenu])

  useEffect(() => {
    setFilteredMenuOptions(menuOptions)
  }, [menuOptions])

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchText = event.target.value.trim()

      if (!searchText) {
        setFilteredMenuOptions(menuOptions)
        return
      }

      const normalizedSearchText = normalize(searchText).toLowerCase()

      const filteredOptions = menuOptions
        .map(option => ({
          ...option,
          items: (option?.items ?? []).filter((item: any) =>
            normalize(item.title).toLowerCase().includes(normalizedSearchText)
          )
        }))
        .filter(option => option.items.length > 0)

      setFilteredMenuOptions(filteredOptions.length > 0 ? filteredOptions : [])
    },
    [menuOptions]
  )

  return {
    userMenu,
    loading,
    connectionKey,
    filteredMenuOptions,
    handleSearch
  }
}
